import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";

import Breadcrumb from "../Breadcrumb";
import SearchBar from "./SearchBar";
import ActionButtons from "./ActionButtons";
import DeviceTable from "./DeviceTable";
import { changeFriendlyName, changeTagsForDevice, confirmNebulaChange, connectComputer, deleteDevice, deviceConnectUserName, getAllDeviceAccessInfo, getComputers, getComputersCount, getConfig, getProfileList, getRolesList, performScanforDevice, updateGateway } from "./Api/EndpointApi";

import useUserStore from "../../../services/userStore";
import { Computer, ConnectionInfo, Device, DeviceLoadingState, RecordingTogglePayload, Tag } from "../interfaces/EndpointInterfaces";
import { Monitor, Plus, Search, Server } from "lucide-react";
import { Button } from "../../ui/button";
import AddManualDevice from "./AddManualDevice";
import { useToast } from "../../../hooks/use-toast";
import { t } from "i18next";
import { Role } from "../interfaces/CommonInterfaces";
import { createGuacConnection, toggleRecordingAPI, toggleRecordingStatus } from "./Api/computerApi";
import { transformApiToDevice } from "./helper";
import TenantEndpointDevices from "../TenantUserEndpoints/TenantEndpointDevice";
import { myLocalStorage } from "../../StorageHelper";
import { checkIfUserHassAccess } from "../../../services/Helpers";
import useDebounce from "../../../utils/useDebounce";
import TenantLists from "../TenantLists";
import { Input } from "../../ui/input";
import TenantDeviceTable from "../TenantUserEndpoints/TenantDeviceTable";
import useNotificationStore from "../../../services/notificationStore";




export default function DeviceManagementDashboard() {
  const { role, email, userId, userRole } = useUserStore((state) => state.user);

  const [searchQuery, setSearchQuery] = useState("");
  const [selectedTenant, setSelectedTenant] = useState("");
  const [selectedDevices, setSelectedDevices] = useState<string[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [deviceList, setDeviceList] = useState<Device[]>([]);
  const [page, setPage] = useState<number>(1);
  const [rowsPerPage, setRowsPerPage] = useState<number>(15);
  const [showAddDeviceDialog, setShowAddDeviceDialog] = useState(false);
  const [lastSelectedDevice, setLastSelectedDevice] = useState<Device | null>(null);
  const [showSelectedOnly, setShowSelectedOnly] = useState(false);
  const [totalDevicesCount, setTotalDevicesCount] = useState<number>(0);
  const [roles, setRoles] = useState<Role[]>([]);
  const setSessionTab = useUserStore((state) => state.setSessionTab);
  const [adminAccess, setAdminAccess] = useState("ALL_MACHINES");
  const { toast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const [deviceLoadingState, setDeviceLoadingState] = useState<Record<string, DeviceLoadingState>>({});
  const { userApprovedNotifications, setUserNotifications } =
    useNotificationStore();
  const deviceToComputer = (device: Device): Computer => ({
    id: device.id,
    guachost: device.guachost ?? "",
    OperatingSystem: device.os ?? device.OperatingSystem ?? "",
    accessRemainingTime: device.accessRemainingTime,
    whiteswanAccessGroupId: device.whiteswanAccessGroupId,
    profileMode: device.profileMode,
    mode: device.mode,
  });


  const fetchDeviceCount = async () => {
    try {
      let tenantName = myLocalStorage.getItem("latestTenant")?.tenantName;

      const response = await getComputersCount({
        tenantName: tenantName,
        email,
        role,
        userId,
        searchMachine: searchQuery,
      });
      setTotalDevicesCount(response ?? 0);
    } catch (error) {
      console.error("Failed to fetch devices", error);
      setTotalDevicesCount(0)
    } finally {
    }
  }



  const checkAccessByGroup = async (deviceList: any, page: number, limit: number, searchQuery: string) => {
    let tenantName = myLocalStorage.getItem("latestTenant")?.tenantName;

    const accessInfoList = await getAllDeviceAccessInfo(
      email,
      page,
      limit,
      searchQuery,
      tenantName
    );
    const enrichedDevices = deviceList.map((device: any) => {
      const access = accessInfoList.find((a: any) => a.deviceId === device.id);
      if (
        access &&
        checkIfUserHassAccess(access?.adComputerUserGroupMembership)
      ) {
        return {
          ...device,
          hasAccess: true,
          whiteswanAccessGroupId: access?.adComputerUserGroupMembership?.id,
          userId: userId,
          isWhiteswanAccess: true,
          accessRemainingTime:
            access?.adComputerUserGroupMembership?.remainingTime,
        };
      }

      return {
        ...device,
        whiteswanAccessGroupId: access?.adComputerUserGroupMembership?.id,
        isWhiteswanAccess: true,
      };
    });
    return enrichedDevices;
  }

  const fetchDeviceList = useCallback(async (isFirst = false, from = "1") => {
    if (isFirst) setLoading(true);
    try {
      let tenantName = myLocalStorage.getItem("latestTenant")?.tenantName;
      const response = await getComputers({
        tenantName: tenantName,
        email,
        role,
        userId,
        page: page - 1,
        limit: rowsPerPage,
        searchMachine: searchQuery,
      });
      const devicesSortedById = response.sort((a, b) =>
        a.id > b.id ? 1 : -1,
      );

      if (role === "TENANT_USER") {

        const newDeviceList = await checkAccessByGroup(
          devicesSortedById,
          page - 1,
          rowsPerPage,
          searchQuery,
        );
        setDeviceList(transformApiToDevice(newDeviceList));
      } else {
        setDeviceList(transformApiToDevice(devicesSortedById));
      }



    } catch (error) {
      console.error("Failed to fetch devices", error);
      setDeviceList([]);
    } finally {
      setLoading(false);
    }
  }, [selectedTenant, email, role, userId, page, searchQuery, rowsPerPage]);


  const getRoles = async () => {
    const response = await getRolesList(selectedTenant);

    if (response && response.length > 0) {
      const formattedRoles: Role[] = response
        .map((role: { id: number; roleName: string }) => ({
          id: String(role.id),
          name: role.roleName,
        }))
        .filter((role: Role) =>
          role.name !== "epam-user" &&
          role.name !== "super-user" &&
          role.name !== "unassigned-user"
        );

      setRoles(formattedRoles);
    } else {
      setRoles([]);
    }
  };

  const getGeneralSettings = async (name: string) => {
    try {
      const response = await getConfig(selectedTenant);
      if (!response) {
        setAdminAccess("ALL_MACHINES");
        return;
      }
      const configValue = response.find(
        (config: { settingsName: string; settingsValue?: string }) =>
          config.settingsName === name
      );

      if (configValue?.settingsValue) {
        setAdminAccess(configValue.settingsValue);
      } else {
        setAdminAccess("ALL_MACHINES");
      }
    } catch (error) {
      console.error("Failed to fetch general settings", error);
    }
  }


  const updateFriendlyName = async (deviceId: string, friendlyName: string, lastSelectedDevice: Device | null) => {
    const response = await changeFriendlyName({
      tenantName: selectedTenant,
      deviceId,
      friendlyName
    });
    if (response) {
      setLastSelectedDevice(null)
      fetchDeviceList(false, "2");
      toast({ title: "Friendly Name Updated", description: `Device "${lastSelectedDevice?.name}" friendly name updated successfully.` });

    }
  }

  const updateNebula = async (deviceId: string, enableNebula: boolean, lastSelectedDevice: Device | null) => {
    const response = await confirmNebulaChange({
      deviceId,
      enableNebula
    });
    if (response) {
      toast({ title: "Nebula Status Updated", description: `Device "${lastSelectedDevice?.name}" nebula status updated successfully.` });
      setLastSelectedDevice(null)
    }
    fetchDeviceList(false, "11");
    setLastSelectedDevice(null);
  }


  const setGateway = async (deviceId: string, selectedSubnets: string[], label: string) => {

    const response = await updateGateway({
      deviceId,
      selectedSubnets
    });
    if (response) {
      setLastSelectedDevice(null)
      toast({ title: label && label.length === 0 ? "Configured Subnet " : label, description: `Device "${lastSelectedDevice?.name}"` });

    }


    fetchDeviceList(false, "10");
    setLastSelectedDevice(null);
  }


  const updateTags = async (deviceId: string, existingTags: Tag[], newTags: Tag[]) => {
    try {
      const response = await changeTagsForDevice({
        deviceId,
        existingTags,
        newTags,
        tenantName: selectedTenant,
      });

      fetchDeviceList(false, "9");
      toast({ title: "Tags Updated", description: `Device "${lastSelectedDevice?.name}" tags updated successfully.` });

    } catch (error) {
      console.error("Error updating tags:", error);
    }
  }

  const performScan = async (deviceId: string,) => {
    const response = await performScanforDevice(deviceId);
    if (response) {
      toast({ title: "Scan Progress", description: `Device "${lastSelectedDevice?.name}" scan started successfully.` });

    }
    setLastSelectedDevice(null);
  }

  const deleteMachine = async (deviceId: string, lastSelectedDevice: Device | null) => {
    const response = await deleteDevice(deviceId);
    if (response) {
      toast({ title: "Device Deleted", description: `Device "${lastSelectedDevice?.name}" deleted successfully.` });
      setLastSelectedDevice(null);
      fetchDeviceList(false, "8");
    }
    setLastSelectedDevice(null);
  }

  const updateDeviceConnectUsername = async (userId: number, username: string, role: string, deviceId: string, port: number) => {
    try {
      const response = await deviceConnectUserName({
        userId,
        username,
        role,
        deviceId,
        accessPort: String(port)
      });
      if (response) {
        fetchDeviceList(false, "7");
      }
    } catch (error) {
      console.error("Failed to update device connect username", error);
    }
  }


  const handleConnect = async (device: Device, mode: string, label: string) => {

    let deviceId = device?.id;
    setDeviceLoadingState((prevState: any) => ({
      ...prevState,
      [deviceId]: {
        loading: true,
        message: "Connecting... Please wait."
      }
    }));
    const computer: Computer = deviceToComputer(device);


    try {
      const userType = role === "TENANT_USER" ? "user" : "admin";
      await connectComputer(computer, email, userType, mode, label, userId, setSessionTab, userApprovedNotifications);
      setDeviceLoadingState((prevState: any) => ({
        ...prevState,
        [deviceId]: {
          loading: false,
          message: "Redirecting... Please wait."
        }
      }));
    } catch (error) {
      console.error("Failed to connect:", error);
      setDeviceLoadingState((prevState: any) => ({
        ...prevState,
        [deviceId]: {
          loading: false,
          message: "Connection failed. Please try again."
        }
      }));
    }

  };

  const handleRecordingToggle = async (payload: RecordingTogglePayload) => {
    let tenantName = myLocalStorage.getItem("latestTenant")?.tenantName;

    const computer: Computer = deviceToComputer(payload.device!);
    const connectionData = await createGuacConnection(computer, "", "", "admin");
    if (!connectionData) return;
    await toggleRecordingAPI(payload, connectionData);
    await toggleRecordingStatus(payload.device!.id, tenantName);
    fetchDeviceList(false, "6");
  };


  useEffect(() => {

    const initData = async (toLoad = true) => {
      if (selectedTenant) {
        if (role === "TENANT_USER") {
          await Promise.all([
            fetchDeviceList(toLoad, "4")
          ]);
        } else {
          await Promise.all([
            fetchDeviceList(toLoad, "5"),
            getRoles(),
          ]);
        }
        getGeneralSettings("endpointConfig")
      }

    };

    const interval = setInterval(() => {
      initData(false);
    }, 10000)
    initData(true);
    return () => clearInterval(interval);
  }, [selectedTenant, email, role, userId, page, searchQuery, rowsPerPage, fetchDeviceList]);


  useEffect(() => {
    fetchDeviceCount();
  }, [selectedTenant, email, role, userId, searchQuery])



  const handleSearch = (query: string) => {
    setSearchQuery(query);
    setPage(1);
    fetchDeviceList(false, "3");
  };

  const handleTenantChange = (tenant: string) => {
    setSelectedTenant(tenant);
  };

  const handleDeviceSelect = (deviceId: string, selected: boolean) => {
    setSelectedDevices((prev) =>
      selected ? [...prev, deviceId] : prev.filter((id) => id !== deviceId)
    );

    if (selected) {
      const device = deviceList.find((d) => d.id === deviceId) || null;
      setLastSelectedDevice(device);
    } else if (lastSelectedDevice?.id === deviceId) {
      setLastSelectedDevice(null);
      setShowSelectedOnly(false);
    }
  };

  useEffect(() => {
    if (!lastSelectedDevice && showSelectedOnly) {
      setShowSelectedOnly(false);
    }
  }, [lastSelectedDevice, showSelectedOnly]);


  const debouncedSearchQuery = useDebounce(searchQuery, 500);  // Use debouncing
  const filteredDevices = useMemo(() => {
    return deviceList.filter(
      (device) =>
        device.name.toLowerCase().includes(debouncedSearchQuery.toLowerCase()) ||
        device.os.toLowerCase().includes(debouncedSearchQuery.toLowerCase()) ||
        device.ipAddresses.some((ip) => ip.includes(debouncedSearchQuery)) ||
        device.tags.some((tag) => tag.name.toLowerCase().includes(debouncedSearchQuery.toLowerCase()))
    );
  }, [deviceList, debouncedSearchQuery]);

  const visibleDevices = useMemo(() => {
    if (showSelectedOnly && lastSelectedDevice) {
      return filteredDevices.filter((device) => device.id === lastSelectedDevice.id);
    }
    return filteredDevices;
  }, [filteredDevices, lastSelectedDevice, showSelectedOnly]);


  return (
    <>
      {role === "TENANT_USER" ?
        <>

          <div className="flex-1 space-y-6 p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900 dark:to-blue-800 rounded-xl flex items-center justify-center border border-blue-200 dark:border-blue-700">
                  <Server className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold tracking-tight" data-testid="text-platform-logs-title">
                    Endpoints
                  </h1>
                  <p className="text-muted-foreground">
                    Devices published to your assigned roles
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <TenantLists onSelectorChange={handleTenantChange} isLabelNeeded={true} />

                <div className="relative w-full xl:w-80">

                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                  <Input
                    placeholder="Search devices..."
                    value={searchQuery}
                    onChange={(e) => handleSearch(e.target.value)}
                    className="pl-10"
                    data-testid="input-search-devices"
                  />
                </div>
              </div>
            </div>



            <TenantDeviceTable
              isLoading={loading}
              devices={filteredDevices}
              onConnect={handleConnect}
              totalDevicesCount={totalDevicesCount}
              deviceLoadingState={deviceLoadingState}
              setPage={setPage}
              page={page}
            />

          </div>

        </>
        :

        <div className="w-full min-w-0 p-6" data-testid="device-management-dashboard">
          <div className="min-w-0 space-y-6">

            <div className="flex flex-col space-y-4 lg:flex-row lg:items-center lg:justify-between lg:space-y-0 lg:space-x-4">
              <SearchBar
                placeholder="Search..."
                onSearch={handleSearch}
                onSelectorChange={handleTenantChange}
                lastSelectedDevice={lastSelectedDevice}
                onTagsChange={(deviceId, existingTags, newTags) => updateTags(deviceId, existingTags, newTags)}
                resetValue={() => setLastSelectedDevice(null)}
              />


              <div className="flex items-center gap-3">
                {!lastSelectedDevice && userRole !== "epam-admin" && <Button
                  onClick={() => setShowAddDeviceDialog(true)}
                  className="bg-blue-600 hover:bg-blue-700 dark:bg-blue-600 dark:hover:bg-blue-700"
                  data-testid="button-add-device"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Device
                </Button>}
                {lastSelectedDevice && <ActionButtons
                  resetValue={() => setLastSelectedDevice(null)}
                  onFriendlyName={(deviceId, friendlyName) => updateFriendlyName(deviceId, friendlyName, lastSelectedDevice)}
                  onDisableConnectivity={(enabled) => updateNebula(lastSelectedDevice.id, enabled, lastSelectedDevice)}
                  onScan={() => performScan(lastSelectedDevice.id)}
                  onGateway={(deviceId, selectedSubnets, label) => setGateway(deviceId, selectedSubnets, label)}
                  onDelete={() => deleteMachine(lastSelectedDevice.id, lastSelectedDevice)}
                  tenantName={selectedTenant || myLocalStorage.getItem("latestTenant")?.tenantName || ""}
                  enabledButtons={{
                    friendlyName: !!lastSelectedDevice,
                    disableConnectivity: !!lastSelectedDevice,
                    scan: !!lastSelectedDevice,
                    delete: !!lastSelectedDevice && lastSelectedDevice.agentVersion !== '1.3.2',
                    gateway: !!lastSelectedDevice && lastSelectedDevice.os.toLowerCase().includes("linux"),
                    tags: !!lastSelectedDevice,
                  }}
                  selectedDevice={lastSelectedDevice}
                />}

              </div>

            </div>
            {role !== "TENANT_USER" && <DeviceTable
              isLoading={loading}
              devices={filteredDevices}
              onConnect={handleConnect}
              onDeviceSelect={handleDeviceSelect}
              selectedTenant={selectedTenant}
              onRecordingToggle={handleRecordingToggle}
              totalDevicesCount={totalDevicesCount}
              adminAccess={adminAccess}
              page={page}
              rowsPerPage={rowsPerPage}
              setPage={setPage}
              setRowsPerPage={setRowsPerPage}
              configureDeviceUsername={(userId, username, role, deviceId, port) => updateDeviceConnectUsername(userId, username, role, deviceId, port)}
              fetchDeviceList={fetchDeviceList}
              roles={roles}
              deviceLoadingState={deviceLoadingState}
              lastSelectedDevice={lastSelectedDevice}
            />}
          </div>
          <AddManualDevice
            open={showAddDeviceDialog}
            onOpenChange={setShowAddDeviceDialog}
            updateEndpoints={() => { }}
          />
        </div>
      }
    </>
  );
}
